// Copyright (c) 2026 Dyna Robotics, Inc. All rights reserved.
// Dyna Robotics Proprietary and Confidential.
// Unauthorized copying, use, or distribution is prohibited.
// SPDX-License-Identifier: LicenseRef-Dyna-Internal

/**
 * Holding the page while a form has work in it that would be lost by leaving.
 *
 * Two ways out, two guards. Moving to another page of the app (a link, Back) goes through
 * the router, which `useBlocker` stops; the caller shows its own confirmation and calls
 * `proceed` or `reset` on the blocker. Reloading, closing the tab or typing another
 * address leaves the app altogether: only the browser's own prompt can stop that, and it
 * shows its own wording — `beforeunload` cannot carry ours.
 */

import { useEffect } from "react";
import { type Blocker, useBlocker } from "react-router-dom";

export function useLeaveGuard(active: boolean): Blocker {
  const blocker = useBlocker(active);

  useEffect(() => {
    if (!active) return;
    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      // Still required by some browsers to show the prompt at all.
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [active]);

  return blocker;
}
